<template>
  <a-drawer
    :width="1280"
    :open="open"
    class="notification-create-drawer"
    :body-style="{ padding: 0 }"
    :footer-style="{ textAlign: 'right', padding: 0 }"
    destroy-on-close
    @close="handleClose"
  >
    <template #title>
      <span class="notification-create-drawer__title">Новое уведомление</span>
    </template>

    <template #closeIcon>
      <span class="notification-create-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <div class="notification-create-drawer__layout">
      <section class="notification-create-drawer__recipients">
        <div class="notification-create-drawer__tabs" role="tablist" aria-label="Тип получателей">
          <button
            type="button"
            class="btn btn-medium"
            :class="recipientTab === 'residents' ? 'btn-black' : 'btn-outline'"
            role="tab"
            :aria-selected="recipientTab === 'residents'"
            @click="recipientTab = 'residents'"
          >
            Резиденты
          </button>
          <button
            type="button"
            class="btn btn-medium"
            :class="recipientTab === 'clusters' ? 'btn-black' : 'btn-outline'"
            role="tab"
            :aria-selected="recipientTab === 'clusters'"
            @click="recipientTab = 'clusters'"
          >
            Кластеры
          </button>
        </div>

        <SearchInput
          v-model="recipientSearch"
          class="notification-create-drawer__search"
          placeholder="Найти..."
          :debounce="0"
        />

        <div
          ref="recipientsTableWrapperRef"
          class="notification-create-drawer__recipients-table-wrap"
        >
          <a-table
            class="notification-create-drawer__recipients-table"
            :columns="recipientColumns"
            :data-source="recipientTableRows"
            :row-selection="rowSelection"
            :pagination="false"
            :bordered="true"
            :scroll="recipientsTableScrollY ? { y: recipientsTableScrollY } : undefined"
            row-key="key"
            size="middle"
          />
        </div>
      </section>

      <a-divider type="vertical" class="notification-create-drawer__divider" />

      <section class="notification-create-drawer__content">
        <a-form layout="vertical" class="notification-create-drawer__form">
          <a-form-item class="stc-ant-field-skin" required>
            <template #label>
              <span class="notification-create-drawer__label">Тема</span>
            </template>
            <a-input
              v-model:value="form.subject"
              placeholder="Тема уведомления"
              :maxlength="SUBJECT_MAX_LENGTH"
            />
          </a-form-item>

          <a-form-item
            class="stc-ant-field-skin notification-create-drawer__message-field"
            required
          >
            <template #label>
              <span class="notification-create-drawer__label">Текст уведомления</span>
            </template>
            <a-textarea
              v-model:value="form.message"
              placeholder="Текст"
              :maxlength="MESSAGE_MAX_LENGTH"
              :auto-size="{ minRows: 8, maxRows: 8 }"
            />
            <div class="notification-create-drawer__textarea-counter">
              {{ form.message.length }} / {{ MESSAGE_MAX_LENGTH }}
            </div>
          </a-form-item>

          <div class="notification-create-drawer__files">
            <FormFileUpload ref="fileUploadRef" v-model="fileItems" />
          </div>
        </a-form>
      </section>
    </div>

    <template #footer>
      <div class="notification-create-drawer__footer">
        <button
          type="button"
          class="btn btn-secondary btn-medium"
          :disabled="loading || !hasChanges"
          @click="handleReset"
        >
          Сбросить
        </button>
        <button
          type="button"
          class="btn btn-primary btn-medium"
          :disabled="loading || !canSubmit"
          @click="handleSubmit"
        >
          {{ loading ? 'Отправка...' : 'Применить' }}
        </button>
      </div>
    </template>
  </a-drawer>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import type { TableColumnsType } from 'ant-design-vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import SearchInput from '../SearchInput.vue';
import FormFileUpload, { type FileUploadItem } from '@/js/components/form/form-file-upload.vue';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
  showStcWarningNotification,
} from '@/js/components/stc-notification';
import { submitManagerNotification } from './manager-notification-api';
import type { NotificationRecipientsData } from './types';

type RecipientTab = 'residents' | 'clusters';

type ResidentTableRow = {
  key: string;
  name: string;
};

type ClusterTableRow = {
  key: string;
  name: string;
};

const SUBJECT_MAX_LENGTH = 255;
const MESSAGE_MAX_LENGTH = 512;

const props = defineProps<{
  open: boolean;
  recipients: NotificationRecipientsData;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'submit'): void;
}>();

const loading = ref(false);
const recipientTab = ref<RecipientTab>('residents');
const recipientSearch = ref('');
const selectedResidentIds = ref<string[]>([]);
const selectedClusterIds = ref<string[]>([]);
const fileItems = ref<FileUploadItem[]>([]);
const fileUploadRef = ref<InstanceType<typeof FormFileUpload> | null>(null);
const recipientsTableWrapperRef = ref<HTMLElement | null>(null);
const recipientsTableScrollY = ref<number>();

let recipientsTableResizeObserver: ResizeObserver | null = null;

const form = ref({
  subject: '',
  message: '',
});

const canSubmit = computed(
  () =>
    form.value.subject.trim() !== '' &&
    form.value.message.trim() !== '' &&
    (selectedResidentIds.value.length > 0 || selectedClusterIds.value.length > 0),
);

const hasChanges = computed(
  () =>
    form.value.subject.trim() !== '' ||
    form.value.message.trim() !== '' ||
    selectedResidentIds.value.length > 0 ||
    selectedClusterIds.value.length > 0 ||
    fileItems.value.length > 0,
);

const residentColumns: TableColumnsType<ResidentTableRow> = [
  {
    title: 'Резидент',
    dataIndex: 'name',
    key: 'name',
  },
];

const clusterColumns: TableColumnsType<ClusterTableRow> = [
  {
    title: 'Кластер',
    dataIndex: 'name',
    key: 'name',
  },
];

const recipientColumns = computed(() =>
  recipientTab.value === 'residents' ? residentColumns : clusterColumns,
);

const residentTableRows = computed<ResidentTableRow[]>(() => {
  const query = recipientSearch.value.trim().toLowerCase();

  return props.recipients.residents
    .filter((resident) => !query || resident.name.toLowerCase().includes(query))
    .map((resident) => ({
      key: resident.id,
      name: resident.name,
    }));
});

const clusterTableRows = computed<ClusterTableRow[]>(() => {
  const query = recipientSearch.value.trim().toLowerCase();

  return props.recipients.clusters
    .filter((cluster) => !query || cluster.name.toLowerCase().includes(query))
    .map((cluster) => ({
      key: cluster.id,
      name: cluster.name,
    }));
});

const recipientTableRows = computed(() =>
  recipientTab.value === 'residents' ? residentTableRows.value : clusterTableRows.value,
);

const rowSelection = computed(() => {
  if (recipientTab.value === 'residents') {
    const visibleIds = new Set(residentTableRows.value.map((row) => row.key));

    return {
      selectedRowKeys: selectedResidentIds.value.filter((id) => visibleIds.has(id)),
      onChange: (keys: (string | number)[]) => {
        const hiddenSelected = selectedResidentIds.value.filter((id) => !visibleIds.has(id));
        selectedResidentIds.value = [...hiddenSelected, ...keys.map(String)];
      },
    };
  }

  const selectedClusterKeys = clusterTableRows.value
    .filter((row) => selectedClusterIds.value.includes(row.key))
    .map((row) => row.key);

  return {
    selectedRowKeys: selectedClusterKeys,
    onChange: (keys: (string | number)[]) => {
      selectedClusterIds.value = keys.map(String);
    },
  };
});

function updateRecipientsTableScrollY(): void {
  const wrapper = recipientsTableWrapperRef.value;

  if (!wrapper) {
    return;
  }

  const tableHead = wrapper.querySelector('.ant-table-thead');
  const headHeight = tableHead?.getBoundingClientRect().height ?? 0;
  const nextScrollY = wrapper.clientHeight - headHeight;

  recipientsTableScrollY.value = nextScrollY > 0 ? nextScrollY : undefined;
}

function setupRecipientsTableResizeObserver(): void {
  teardownRecipientsTableResizeObserver();

  const wrapper = recipientsTableWrapperRef.value;

  if (!wrapper) {
    return;
  }

  if (typeof ResizeObserver === 'undefined') {
    updateRecipientsTableScrollY();
    return;
  }

  recipientsTableResizeObserver = new ResizeObserver(() => {
    updateRecipientsTableScrollY();
  });
  recipientsTableResizeObserver.observe(wrapper);
  updateRecipientsTableScrollY();
}

function teardownRecipientsTableResizeObserver(): void {
  recipientsTableResizeObserver?.disconnect();
  recipientsTableResizeObserver = null;
}

watch(
  () => props.open,
  async (isOpen) => {
    if (!isOpen) {
      teardownRecipientsTableResizeObserver();
      recipientsTableScrollY.value = undefined;
      resetFormState();
      return;
    }

    await nextTick();
    setupRecipientsTableResizeObserver();
  },
);

watch([recipientTab, recipientTableRows], async () => {
  if (!props.open) {
    return;
  }

  await nextTick();
  updateRecipientsTableScrollY();
});

onBeforeUnmount(() => {
  teardownRecipientsTableResizeObserver();
});

function resetFormState(): void {
  form.value = { subject: '', message: '' };
  selectedResidentIds.value = [];
  selectedClusterIds.value = [];
  recipientSearch.value = '';
  recipientTab.value = 'residents';
  fileUploadRef.value?.reset();
}

function handleReset(): void {
  resetFormState();
}

function handleClose(): void {
  emit('update:open', false);
}

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) {
    return;
  }

  const readyFiles = fileUploadRef.value?.getReadyFiles() ?? [];
  const hasUploadingFiles = fileItems.value.some((item) => item.status === 'uploading');

  if (hasUploadingFiles) {
    showStcWarningNotification('Дождитесь окончания загрузки файлов');
    return;
  }

  loading.value = true;

  try {
    await submitManagerNotification({
      subject: form.value.subject.trim(),
      message: form.value.message.trim(),
      residents: selectedResidentIds.value.length ? selectedResidentIds.value : undefined,
      clusters: selectedClusterIds.value.length ? selectedClusterIds.value : undefined,
      files: readyFiles,
    });

    showStcSuccessNotification('Уведомление отправлено');
    emit('update:open', false);
    emit('submit');
  } catch (error) {
    showStcErrorNotification(
      error instanceof Error ? error.message : 'Не удалось отправить уведомление',
    );
  } finally {
    loading.value = false;
  }
}
</script>
